import React, { useState } from 'react';
import axios from 'axios';
import { Sparkles, Loader2, Wand2, X, Check } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * AIRewritePosteModal — Quick rewrite of an existing poste description via Claude.
 * Props:
 *   - open: bool
 *   - onClose: () => void
 *   - currentText: string
 *   - lot: string
 *   - onAccept: (newText: string) => void   (receives combined "TITRE\nDESCRIPTION")
 */
const AIRewritePosteModal = ({ open, onClose, currentText, lot = '', onAccept }) => {
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState(null); // {titre, description, raw}
  const [refine, setRefine] = useState('');

  React.useEffect(() => {
    if (open) {
      setOutput(null);
      setRefine('');
      // auto-generate on open
      generate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const generate = async (refineText = null) => {
    if (!currentText || currentText.trim().length < 3) {
      toast.error("Texte trop court pour être réécrit");
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const body = { text: currentText, type: 'SPECIFIQUE', lot };
      if (refineText && output) {
        body.previous_output = output.raw;
        body.refine_instructions = refineText;
      }
      const res = await axios.post(`${BACKEND}/api/ai/generate-description`, body, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 90000  // 90s for Claude
      });
      setOutput(res.data);
      if (refineText) setRefine('');
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur IA");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const combined = output ? `${output.titre}\n${output.description}` : '';

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ai-rewrite-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-violet-50 to-indigo-50">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-600" />
            <h2 className="text-lg font-bold text-slate-900">Réécrire avec l'IA</h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/60 rounded-lg">
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Current */}
          <div>
            <p className="text-xs text-slate-500 uppercase mb-1 font-medium">Texte actuel</p>
            <div className="text-sm bg-slate-50 border border-slate-200 rounded-lg p-3 whitespace-pre-wrap text-slate-700">
              {currentText || <span className="italic text-slate-400">(vide)</span>}
            </div>
          </div>

          {/* AI Output */}
          <div>
            <p className="text-xs text-violet-700 uppercase mb-1 font-medium flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Proposition IA
            </p>
            <div className="text-sm bg-violet-50 border border-violet-200 rounded-lg p-3 min-h-[80px]" data-testid="ai-rewrite-output">
              {loading && !output && (
                <div className="flex items-center gap-2 text-violet-600">
                  <Loader2 className="w-4 h-4 animate-spin" /> Génération en cours…
                </div>
              )}
              {output && (
                <div className="space-y-2">
                  <div className="font-bold text-slate-900">{output.titre}</div>
                  <div className="whitespace-pre-wrap text-slate-700">{output.description}</div>
                </div>
              )}
            </div>
          </div>

          {/* Refine */}
          {output && (
            <div className="bg-violet-50/60 border border-violet-100 rounded-lg p-3 space-y-2">
              <label className="text-xs font-medium text-violet-800 flex items-center gap-1">
                <Wand2 className="w-3 h-3" /> Correction à appliquer (optionnel)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={refine}
                  onChange={(e) => setRefine(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && refine.trim()) { e.preventDefault(); generate(refine.trim()); } }}
                  placeholder="Ex: plus concis, ajoute la dépose, style plus pro…"
                  className="flex-1 h-9 px-3 text-sm border border-violet-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-violet-400"
                  disabled={loading}
                  data-testid="ai-rewrite-refine-input"
                />
                <button
                  onClick={() => generate(refine.trim())}
                  disabled={loading || !refine.trim()}
                  className="h-9 px-4 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 disabled:opacity-60"
                  data-testid="ai-rewrite-refine-submit"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Appliquer'}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-200 bg-slate-50 flex flex-wrap gap-2 justify-end">
          <button
            onClick={onClose}
            className="px-4 h-10 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-white"
            data-testid="ai-rewrite-cancel"
          >
            Annuler
          </button>
          <button
            onClick={() => generate()}
            disabled={loading}
            className="px-4 h-10 border border-violet-300 text-violet-700 rounded-lg text-sm font-medium hover:bg-violet-50 inline-flex items-center gap-1.5 disabled:opacity-60"
            data-testid="ai-rewrite-regenerate"
          >
            <Sparkles className="w-4 h-4" /> Regénérer
          </button>
          <button
            onClick={() => { onAccept(combined); onClose(); }}
            disabled={!output || loading}
            className="px-4 h-10 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-lg text-sm font-medium hover:from-violet-700 hover:to-indigo-700 inline-flex items-center gap-1.5 disabled:opacity-60"
            data-testid="ai-rewrite-accept"
          >
            <Check className="w-4 h-4" /> Remplacer
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIRewritePosteModal;
